/* global customElements */
import WarpElement from '@warp-ds/elements-core';
import { css, html } from 'lit';
import pulse from '@finn-no/pulse-sdk';
import '@warp-ds/icons/elements/link-external-16';
import '@warp-ds/icons/elements/chevron-right-16';

export class CategoryExplorer extends WarpElement {
	static styles = [
		...WarpElement.styles, // Adds the global styles
		css`
			/** The line below is where the injected CSS goes, removing it means you get no CSS from the design system **/
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.h1{font-weight:700;font-size:var(--w-font-size-xxl);line-height:var(--w-line-height-xxl)}.h2{font-weight:700;font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}.h3{font-weight:700;font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}.hover\\:bg-\\[--w-color-pill-suggestion-background-hover\\]:hover{background-color:var(--w-color-pill-suggestion-background-hover)}.active\\:bg-\\[--w-color-pill-suggestion-background-active\\]:active{background-color:var(--w-color-pill-suggestion-background-active)}.border-t{border-top-width:1px}.first\\:border-transparent:first-child{border-color:#0000}.no-underline,.hover\\:no-underline:hover,.focus\\:no-underline:focus,.active\\:no-underline:active{text-decoration:none}.flex-wrap{flex-wrap:wrap}.focus\\:focusable:focus,.focusable:focus,.focusable:focus-visible{outline:2px solid var(--w-s-color-border-focus);outline-offset:var(--w-outline-offset,1px)}.focusable:not(:focus-visible){outline:none}.justify-start{justify-content:flex-start}.place-self-center{place-self:center}.static{position:static}.s-icon-primary{color:var(--w-s-color-icon-primary)}.s-icon-subtle{color:var(--w-s-color-icon-subtle)}.part-\\[w-icon-link-external-16-part\\]\\:h-12::part(w-icon-link-external-16-part){height:1.2rem}.part-\\[w-icon-link-external-16-part\\]\\:w-12::part(w-icon-link-external-16-part){width:1.2rem}.max-w-\\[624\\]{max-width:62.4rem}.my-24{margin-top:2.4rem;margin-bottom:2.4rem}.mb-24{margin-bottom:2.4rem}.mb-8{margin-bottom:.8rem}.ml-16{margin-left:1.6rem}.mt-24{margin-top:2.4rem}@media (min-width:768px){.md\\:justify-start{justify-content:flex-start}.md\\:my-48{margin-top:4.8rem;margin-bottom:4.8rem}};
		`,
	];

	static properties = { catExData: { type: String } };

	_trackViewEvent(trackingData) {
		pulse.trackPageView({
			object: {
				type: trackingData.object.type,
				pageType: trackingData.object.page_type,
			},
			vertical: {
				'@type': trackingData.vertical.type,
				name: trackingData.vertical.name,
			},
		});
	}

	_trackClickEvent(trackingData) {
		pulse.trackEvent({
			type: 'Click',
			intent: 'Open',
			name: trackingData.name,
			object: {
				type: 'UIElement',
				elementType: trackingData.object.element_type,
				name: trackingData.object.name,
			},
			target: trackingData.target,
			vertical: {
				name: trackingData.vertical.name,
				'@type': trackingData.vertical.type,
			},
		});
	}

	catExSection(section) {
		return html`
			<section class="flex flex-col border-t first:border-transparent mb-24">
				<h1 class="h1 mt-24 mb-0">${section.label}</h1>
				<div class="flex flex-wrap justify-start mt-16">
					${section.subtree.map((subSection) =>
						subSection.node_type === 'h2' ? this.catExSubSection(subSection) : this.catExCategory(subSection),
					)}
				</div>
			</section>
		`;
	}

	catExSubSection(subSection) {
		return html` <div class="flex flex-col">
			<a
				class="flex items-center justify-between md:justify-start s-text h3 mt-16 no-underline focus:no-underline focus:focusable"
				href="${subSection.destinations?.url}"
				@click="${() => this._trackClickEvent(subSection.tracking)}"
				target="${subSection.force_external ? '_blank' : ''}"
				>${subSection.label}
				${subSection.force_external
					? html` <w-icon-link-external-16 class="s-icon-primary ml-16"></w-icon-link-external-16>`
					: html` <w-icon-chevron-right-16 class="s-icon-primary ml-16"></w-icon-chevron-right-16>`}</a
			>
			<div class="flex flex-wrap justify-start mt-16">
				${subSection.subtree?.map((category) => this.catExCategory(category))}
			</div>
		</div>`;
	}

	catExCategory(category) {
		return html`
			<a
				role="button"
				target="${category.force_external ? '_blank' : ''}"
				class="mr-8 mb-8 inline-flex items-center focusable text-s transition-all pl-12 py-8 rounded-l-full bg-[--w-color-pill-suggestion-background] hover:bg-[--w-color-pill-suggestion-background-hover] active:bg-[--w-color-pill-suggestion-background-active] s-text font-bold pr-12 rounded-r-full active:no-underline hover:no-underline"
				href="${category.destinations.url}"
				@click="${() => this._trackClickEvent(category.tracking)}"
			>
				${category.label}
				${category.force_external
					? html` <w-icon-link-external-16
							class="s-icon-subtle ml-8 part-[w-icon-link-external-16-part]:w-12 part-[w-icon-link-external-16-part]:h-12"
						></w-icon-link-external-16>`
					: html``}
			</a>
		`;
	}

	render() {
		const catExData = JSON.parse(this.catExData);

		if (typeof window !== 'undefined') {
			this._trackViewEvent(catExData.tracking);
		}

		return html`
			<div class="flex flex-col">
				<div class="my-24 md:my-48 max-w-[624] place-self-center" data-categories-pulse-wrapper>
					${catExData?.catex_tree?.map((section) => this.catExSection(section))}
				</div>
			</div>
		`;
	}
}

if (!customElements.get('category-explorer')) {
	customElements.define('category-explorer', CategoryExplorer);
}
